import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import OutlinedButton from 'components/application/buttons/OutlinedButton';

import {
  formatOpeningHours,
  formatServiceAge,
} from 'components/medicalLocations/helpers';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function MedicalLocationSelectItem({
  assignableMedicalLocation,
  onSelect,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'medicalLocations.MedicalLocationSelectItem',
  });

  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'medicalLocations.helpers',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    attributes: {
      address1,
      address2,
      city,
      maxAge,
      minAge,
      name,
      openingHours,
      phoneNumber,
      postcode,
      source,
    },
    id: assignableMedicalLocationId,
    meta: { distance },
  } = assignableMedicalLocation;

  const formattedOpeningHours = formatOpeningHours({
    hours: openingHours,
    translations: i18nTHelpers('formatOpeningHours', { returnObjects: true }),
  });

  return (
    <div className='tw-mt-2 tw-flex tw-w-full tw-max-w-xl tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-200 tw-bg-white tw-p-4'>
      <div className='tw-mr-5 tw-flex tw-w-2/4 tw-flex-col tw-justify-between tw-gap-4'>
        <div>
          <p className='tw-m-0 tw-text-l tw-font-bold tw-text-grey-900'>
            {name}
          </p>
          <p className='tw-m-0 tw-text-m tw-text-grey-700'>
            {distance &&
              i18nTShared(
                `milesWithCount_${parseFloat(distance).toFixed(0) === 1 ? 'one' : 'other'}`,
                { count: parseFloat(distance).toFixed(1) },
              )}
            {distance && openingHours && '・'}
            {formattedOpeningHours}
          </p>
        </div>
        {source === 'nhs' && (
          <p className='tw-m-0 tw-text-s tw-text-grey-700'>
            {i18nT('services.label')}{' '}
            {formatServiceAge({
              maxAge,
              minAge,
              translations: i18nTHelpers('formatServiceAge', {
                maxAge,
                minAge,
                returnObjects: true,
              }),
            })}
          </p>
        )}
      </div>

      <div className='tw-ml-2 tw-w-1/4'>
        <div className='tw-text-s tw-text-grey-700'>
          {address1 && <p className='tw-m-0'>{address1},</p>}
          {address2 && <p className='tw-m-0'>{address2},</p>}
          <p className='tw-m-0'>
            {city && `${city}, `}
            {postcode}
          </p>
        </div>
        {phoneNumber && <div className='tw-mt-2 tw-text-s'>{phoneNumber}</div>}
      </div>

      <div className='tw-flex tw-w-1/4 tw-justify-end'>
        <OutlinedButton
          className='tw-ml-2 tw-w-max tw-min-w-[80px] tw-justify-center'
          color='grey'
          onClick={() => onSelect(assignableMedicalLocationId)}
          size='sm'
        >
          {i18nTShared('select')}
        </OutlinedButton>
      </div>
    </div>
  );
}

MedicalLocationSelectItem.propTypes = {
  assignableMedicalLocation: resourceShape('assignableMedicalLocation')
    .isRequired,
  onSelect: PropTypes.func.isRequired,
};
